import {
  AlertIcon,
  DownloadIcon,
  IconButton,
  Menu,
  MenuItem,
  MenuSeparator,
  MoreIcon,
  ShareIcon,
  TrashIcon,
} from '@/shared/ui';

export type ContentHeadProps = {
  title: string;
  /** Formatted creation date, or null when the timestamp is missing/unparseable. */
  createdLabel: string | null;
  /** Style / mode / aspect ratio, already filtered to what the backend sent. */
  details?: string[];
  /** Download is only offered once there is a file to download. */
  canDownload: boolean;
  downloading: boolean;
  /** Cancel is only offered while the job can still be stopped. */
  isRendering: boolean;
  cancelling: boolean;
  /** Deletion is the other half of that condition — see the menu below. */
  deleting: boolean;
  onDownload: () => void;
  onShare?: () => void;
  /** Offered only when social exports are available for this video. */
  onExport?: () => void;
  onReportIssue: () => void;
  onCancel: () => void;
  onDelete: () => void;
};

/**
 * Title, date, and the overflow menu of everything you can do to this video.
 *
 * Every item is conditional, and the conditions are the point: Download appears
 * only when there is a file, Cancel only while the job is still running, and
 * the separator travels with Cancel rather than sitting above a menu whose last
 * group may be empty.
 *
 * Three siblings, not two. The menu used to sit inside `.content-page__head-meta`
 * beside the date, which is fine while the header is one wide row and is the
 * whole bug on a phone: the header stacks at 720px, the meta row is then sized
 * to its own content rather than to the card, and a row that cannot wrap is as
 * wide as the date, the details and the button laid end to end — 451px of it
 * inside a 324px card, clipped rather than scrolled, with the menu the part that
 * fell off. Title, meta and menu are now placed individually by
 * `grid-template-areas`, so the narrow layout is a rearrangement (menu up beside
 * the title, meta down onto a line of its own) rather than a row that overflows.
 */
export function ContentHead({
  title,
  createdLabel,
  details = [],
  canDownload,
  downloading,
  isRendering,
  cancelling,
  deleting,
  onDownload,
  onShare,
  onExport,
  onReportIssue,
  onCancel,
  onDelete,
}: ContentHeadProps) {
  const hasMeta = Boolean(createdLabel) || details.length > 0;

  return (
    <header className="content-page__head">
      <h1 className="content-page__title">{title}</h1>

      {/* Rendered only when there is something to say. An empty div would still
          take a grid row and the gap above it once the header stacks. */}
      {hasMeta ? (
        <div className="content-page__head-meta">
          {createdLabel ? <span className="content-page__date">{createdLabel}</span> : null}
          {/* Not folded into the date span: the two are separate facts, and the
              row wraps between them when the card is too narrow for both — at
              which point content.css drops the divider, since a rule down the
              left of an item that has just moved onto its own line is pointing
              at nothing. */}
          {details.length > 0 ? (
            <span className="content-page__gen">{details.join(' · ')}</span>
          ) : null}
        </div>
      ) : null}

      <div className="content-page__head-actions">
        <Menu
          label="More actions"
          trigger={
            <IconButton label="More actions" size="sm" variant="ghost">
              <MoreIcon size={18} />
            </IconButton>
          }
        >
          {canDownload ? (
            <MenuItem icon={<DownloadIcon size={16} />} disabled={downloading} onSelect={onDownload}>
              {downloading ? 'Preparing…' : 'Download video'}
            </MenuItem>
          ) : null}
          {onShare ? (
            <MenuItem icon={<ShareIcon size={16} />} onSelect={onShare}>
              Share
            </MenuItem>
          ) : null}
          {onExport ? (
            <MenuItem icon={<DownloadIcon size={16} />} onSelect={onExport}>
              Export for a platform…
            </MenuItem>
          ) : null}
          <MenuItem icon={<AlertIcon size={16} />} onSelect={onReportIssue}>
            Report an issue
          </MenuItem>
          {/* One destructive item, and which one depends on whether the job is
              still running. Cancel is what a rendering job needs and does
              nothing to a finished one; delete is a different backend call that
              is not offered mid-render. Showing both at once is how the wrong
              one gets picked, and only one of them is reversible. */}
          <MenuSeparator />
          {isRendering ? (
            <MenuItem
              icon={<TrashIcon size={16} />}
              tone="danger"
              disabled={cancelling}
              onSelect={onCancel}
            >
              {cancelling ? 'Cancelling…' : 'Cancel generation'}
            </MenuItem>
          ) : (
            <MenuItem
              icon={<TrashIcon size={16} />}
              tone="danger"
              disabled={deleting}
              onSelect={onDelete}
            >
              {deleting ? 'Deleting…' : 'Delete permanently'}
            </MenuItem>
          )}
        </Menu>
      </div>
    </header>
  );
}
